import { DateColumnHeader } from '../components/DateColumnHeader';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Button, Group } from '@mantine/core';
import { IconFileExcel, IconUpload } from '@tabler/icons-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from 'mantine-react-table';
import { useTranslation } from 'react-i18next';

import { TableExcelExportModal } from '../components/TableExcelExportModal';
import {
  datedExportFilename,
  exportMrtTableToExcel,
  type TableExportConfig,
  type TableExportOptions,
} from './tableExcelExport';
import { withMrtContainsSearch } from './mrtTableSearch';

export type ExportableTableConfig<T extends Record<string, unknown>> = Omit<
  TableExportConfig<T>,
  'filename'
> & {
  filenamePrefix: string;
  showIncludeDeletedColumn?: boolean;
  onOpenImport?: () => void;
};

type UseExportableTableResult<T extends Record<string, unknown>> = {
  table: MRT_TableInstance<T>;
  exportModal: ReactNode;
  openExportModal: () => void;
  exportButton: ReactNode;
};

export function useExportableTable<T extends Record<string, unknown>>(
  options: MRT_TableOptions<T>,
  exportConfig: ExportableTableConfig<T>,
): UseExportableTableResult<T> {
  const { t, i18n } = useTranslation();
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const openExportModal = useCallback(() => {
    setExportModalOpen(true);
  }, []);

  const dateHintColumns = useMemo(() => options.columns.map(column => {
    const key=String(column.accessorKey || column.id || '');
    return /(_date|_at|Date)$/.test(key) && !column.Header
      ? { ...column, Header: <DateColumnHeader label={column.header} language={i18n.language}/> }
      : column;
  }), [options.columns,i18n.language]);

  const table = useMantineReactTable(
    withMrtContainsSearch({
      ...options,
      columns:dateHintColumns,
      enableStickyHeader: true,
      renderTopToolbarCustomActions: (props) => (
      <Group spacing="xs">
        {options.renderTopToolbarCustomActions?.(props)}
        {exportConfig.onOpenImport ? (
          <Button
            variant="light"
            color="brand"
            size="xs"
            leftIcon={<IconUpload size={16} />}
            onClick={exportConfig.onOpenImport}
          >
            {t('common.import.excel')}
          </Button>
        ) : null}
        <Button
          variant="light"
          color="green"
          size="xs"
          leftIcon={<IconFileExcel size={16} />}
          onClick={openExportModal}
        >
          {t('common.export.excel')}
        </Button>
      </Group>
    ),
    }),
  );

  const filename = useMemo(
    () => datedExportFilename(exportConfig.filenamePrefix),
    [exportConfig.filenamePrefix],
  );

  const handleExport = useCallback(
    (exportOptions: TableExportOptions) => {
      const exported = exportMrtTableToExcel(table, exportOptions, {
        ...exportConfig,
        filename,
      });
      if (!exported) {
        alert(t('common.export.noRows'));
        return;
      }
      setExportModalOpen(false);
    },
    [exportConfig, filename, table, t],
  );

  const exportModal = (
    <TableExcelExportModal
      opened={exportModalOpen}
      onClose={() => setExportModalOpen(false)}
      onExport={handleExport}
      showIncludeIds={Boolean(exportConfig.idField)}
      showIncludeDeletedColumn={exportConfig.showIncludeDeletedColumn ?? true}
    />
  );

  const exportButton = (
    <Button
      variant="light"
      color="green"
      size="xs"
      leftIcon={<IconFileExcel size={16} />}
      onClick={openExportModal}
    >
      {t('common.export.excel')}
    </Button>
  );

  return { table, exportModal, openExportModal, exportButton };
}

export function ExportableMantineReactTable<T extends Record<string, unknown>>({
  table,
  exportModal,
  importModal,
}: {
  table: MRT_TableInstance<T>;
  exportModal: ReactNode;
  importModal?: ReactNode;
}) {
  return (
    <>
      <MantineReactTable table={table} />
      {exportModal}
      {importModal}
    </>
  );
}
